'use client';

import { useEffect, useState } from 'react';
import {
  Container,
  IconContainer,
  PayOff,
  Spacer,
  Text,
  TextContainer,
  Title,
} from '@/app/components/search/components/Banners/LoyaltyBanner/styled.ts';
import { DeprecatedButton } from '@lmn/design-system';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import type { LOYALTY_CONTEXT, LOYALTY_POSITION } from '@/app/common/starTrack/events/utils/eventsUtils.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { createLoyaltyBannerClickedPayload } from '@/app/common/starTrack/events/loyaltyBannerClicked.ts';
import { createLoyaltyBannerShownPayload } from '@/app/common/starTrack/events/loyaltyBannerShown.ts';

type LoyaltyBannerProps = {
  loyaltyContext: LOYALTY_CONTEXT;
  loyaltyPosition: LOYALTY_POSITION;
  isLoggedIn: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const getRedirectUrl = (secureDomain: string, currentUrl: string | null) => {
  const backUrl = currentUrl ? `targetUrl=${encodeURIComponent(currentUrl)}` : '';
  return `https://${secureDomain}/auth/loyalty/opt-in?${backUrl}`;
};

const LoyaltyBanner = ({ loyaltyContext, loyaltyPosition, isLoggedIn, ...rest }: LoyaltyBannerProps) => {
  const { staticData, searchInfoData, localization, commonData } = usePageWrapperData();
  const { starTrack } = useCommonClient();
  const { imageBasePath } = localization;
  const { t } = useTranslation();
  const rightSwirl = `${imageBasePath}icons/color/right-swirl.svg`;
  const topSwirl = `${imageBasePath}icons/color/top-swirl.svg`;
  const logo = `${imageBasePath}icons/color/loyalty.svg`;
  const arrow = `${imageBasePath}icons/color/loyalty-right-arrow.svg`;
  const secureDomain = staticData?.domainValues?.secureDomain || 'secure.lastminute.com';
  const [urlRedirect, setUrlRedirect] = useState<string>(getRedirectUrl(secureDomain, null));

  useEffect(() => {
    starTrack.loyaltyBannerShown(
      createLoyaltyBannerShownPayload({
        searchId: searchInfoData.searchId,
        context: loyaltyContext,
        position: loyaltyPosition,
        isLoggedIn,
        localization,
        commonData,
      })
    );
  }, [searchInfoData.searchId, loyaltyContext, loyaltyPosition, isLoggedIn]);

  useEffect(() => {
    // Build URL on client side where window is available
    const currentUrl = canUseDOM ? window?.location?.href : null;
    setUrlRedirect(getRedirectUrl(secureDomain, currentUrl));
  }, [secureDomain]);

  const handleOnClick = () => {
    starTrack.loyaltyBannerClicked(
      createLoyaltyBannerClickedPayload({
        searchId: searchInfoData.searchId,
        context: loyaltyContext,
        position: loyaltyPosition,
        isLoggedIn,
        localization,
        commonData,
      })
    );
  };
  return (
    <Container topSwirl={topSwirl} rightSwirl={rightSwirl} {...rest}>
      <IconContainer>
        <img src={logo} alt="" />
      </IconContainer>
      <Spacer />
      <TextContainer>
        <Title>{t('vacation.oss.cards.loyalty.banner.title')}</Title>
        <Text>{t('vacation.oss.cards.loyalty.banner.body')}</Text>
      </TextContainer>
      <PayOff>
        <span>{t('vacation.oss.cards.loyalty.banner.tagline')}</span> <img src={arrow} alt="" />
      </PayOff>
      <DeprecatedButton
        variant="accent"
        as="a"
        // @ts-ignore
        // TS is complaining about href on DeprecatedButton, but it's valid when "as" is "a"
        // because the component doesn't have a href prop in its type definition,
        // but it gets passed down to the underlying <a> element via props spreading
        href={urlRedirect}
        style={{ flexShrink: 0, fontSize: '16px', lineHeight: '24px' }}
        onClick={handleOnClick}
      >
        {t('vacation.oss.cards.loyalty.banner.cta')}
      </DeprecatedButton>
    </Container>
  );
};

export default LoyaltyBanner;
